import { navigatorIntegrationConfig } from 'src/config';
import {
  createNavigatorMessage,
  createNavigatorRequestId,
  isNavigatorEnvelope,
  normalizeNavigatorOrigin,
} from './NavigatorBridge';

const RELAY_TIMEOUT_MS = 1800;
let activeSignOutHandler = null;
let activeRelayPromise = null;
let activeSignOutPromise = null;

export const registerNavigatorSignOutHandler = (handler) => {
  activeSignOutHandler = typeof handler === 'function' ? handler : null;

  return () => {
    if (activeSignOutHandler === handler) {
      activeSignOutHandler = null;
    }
  };
};

const requestSignOutThroughRelay = () => {
  if (activeRelayPromise) return activeRelayPromise;

  const navigatorOrigin = normalizeNavigatorOrigin(navigatorIntegrationConfig.origin);
  if (
    !navigatorIntegrationConfig.enabled ||
    !navigatorOrigin ||
    typeof document === 'undefined' ||
    !document.body
  ) {
    return Promise.resolve({ status: 'not_connected' });
  }

  activeRelayPromise = new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    const requestId = createNavigatorRequestId();
    let currentInstanceId = null;
    let completed = false;

    const finish = (result) => {
      if (completed) return;
      completed = true;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      iframe.remove();
      activeRelayPromise = null;
      resolve(result);
    };

    const onMessage = (event) => {
      if (
        event.origin !== navigatorOrigin ||
        event.source !== iframe.contentWindow ||
        !isNavigatorEnvelope(event.data)
      ) {
        return;
      }

      const message = event.data;
      if (message.type === 'navigator.ready') {
        currentInstanceId = message.instanceId;
        iframe.contentWindow.postMessage(createNavigatorMessage({
          type: 'pathway.sign_out',
          instanceId: currentInstanceId,
          requestId,
        }), navigatorOrigin);
        return;
      }

      if (
        message.type === 'navigator.signed_out' &&
        message.instanceId === currentInstanceId &&
        message.requestId === requestId
      ) {
        finish({ status: 'signed_out' });
      }
    };

    const timer = window.setTimeout(
      () => finish({ status: 'timeout' }),
      RELAY_TIMEOUT_MS,
    );

    window.addEventListener('message', onMessage);
    iframe.hidden = true;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.title = 'Navigator sign-out relay';
    iframe.referrerPolicy = 'strict-origin';
    // The minimal gate exposes no dashboard data while waiting for sign-out.
    iframe.src = `${navigatorOrigin}/pathway-embed`;
    document.body.appendChild(iframe);
  });

  return activeRelayPromise;
};

const performNavigatorSignOut = async () => {
  if (activeSignOutHandler) {
    try {
      const result = await activeSignOutHandler();
      if (result?.status === 'signed_out') {
        return result;
      }
    } catch (_error) {
      // Fall through to the hidden same-origin relay. A mounted workspace can
      // still be unready during a fast logout or a network retry.
    }
  }

  return requestSignOutThroughRelay();
};

export const requestNavigatorSignOut = () => {
  if (activeSignOutPromise) return activeSignOutPromise;

  activeSignOutPromise = performNavigatorSignOut().finally(() => {
    activeSignOutPromise = null;
  });
  return activeSignOutPromise;
};
